import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup, Alert } from 'reactstrap';
import { format, addDays, isValid, parseISO, startOfDay } from 'date-fns';
import firebase from 'firebase/compat/app';

export interface ProposedDates {
  preferred: string | Date | firebase.firestore.Timestamp;
  secondary: string | Date | firebase.firestore.Timestamp;
  tertiary: string | Date | firebase.firestore.Timestamp;
}

interface InspectionMilestonesProps {
  maxInspectionDays: number;
  isModalOpen: boolean;
  toggleModal: () => void;
  onDatesSet: (dates: ProposedDates) => void;
  onDateSelected: (date: string) => void;
  proposedDates: ProposedDates | null;
  selectedDate: string | Date | firebase.firestore.Timestamp | null;
  isContractor: boolean;
  milestones: any[];
}

const InspectionMilestones: React.FC<InspectionMilestonesProps> = ({
  maxInspectionDays = 14, // Default value
  isModalOpen,
  toggleModal,
  onDatesSet,
  onDateSelected,
  proposedDates,
  selectedDate: existingSelectedDate,
  isContractor,
  milestones
}) => {
  const hasInspectionMilestones = useMemo(() =>
    Array.isArray(milestones) && milestones.some(milestone => milestone.type === 'inspection'),
    [milestones]
  );

  const [preferredDate, setPreferredDate] = useState<string>('');
  const [secondaryDate, setSecondaryDate] = useState<string>('');
  const [tertiaryDate, setTertiaryDate] = useState<string>('');
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [error, setError] = useState<string>('');

  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => {
    try {
      return format(addDays(today, maxInspectionDays), 'yyyy-MM-dd');
    } catch (error) {
      console.error('Error calculating maxDate:', error);
      // Return a safe default max date (e.g., 14 days from now)
      return format(addDays(today, 14), 'yyyy-MM-dd');
    }
  }, [today, maxInspectionDays]);

  const safeDateFormat = useCallback((date: Date | null): string => {
    if (!date || !isValid(date)) return '';
    try {
      return format(date, 'yyyy-MM-dd');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  }, []);

  const convertToDate = useCallback((date: string | Date | firebase.firestore.Timestamp | null): Date | null => {
    if (!date) return null;
    
    try {
      // Handle Firestore Timestamp
      if (date instanceof firebase.firestore.Timestamp) {
        const converted = date.toDate();
        return isValid(converted) ? converted : null;
      }
      
      // Handle Date object
      if (date instanceof Date) {
        return isValid(date) ? date : null;
      }
      
      // Handle ISO string
      if (typeof date === 'string') {
        // Try parsing as ISO string first
        const parsed = parseISO(date);
        if (isValid(parsed)) return parsed;
        
        // If that fails, try new Date()
        const fallback = new Date(date);
        return isValid(fallback) ? fallback : null;
      }
      
      return null;
    } catch (error) {
      console.error('Error converting date:', error, typeof date, date);
      return null;
    }
  }, []);

  const formatDisplayDate = useCallback((date: string | Date | firebase.firestore.Timestamp | null): string => {
    try {
      const converted = convertToDate(date);
      if (!converted || !isValid(converted)) return 'Date not set';
      return format(converted, 'MMM dd, yyyy');
    } catch (error) {
      console.error('Error formatting display date:', error);
      return 'Invalid date';
    }
  }, [convertToDate]);

  useEffect(() => {
    if (isModalOpen) {
      setError('');
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (proposedDates) {
      try {
        const preferred = convertToDate(proposedDates.preferred);
        const secondary = convertToDate(proposedDates.secondary);
        const tertiary = convertToDate(proposedDates.tertiary);

        setPreferredDate(preferred ? safeDateFormat(preferred) : '');
        setSecondaryDate(secondary ? safeDateFormat(secondary) : '');
        setTertiaryDate(tertiary ? safeDateFormat(tertiary) : '');
      } catch (error) {
        console.error('Error setting proposed dates:', error);
        setError('Error processing proposed dates');
      }
    }

    if (existingSelectedDate) {
      try {
        const selected = convertToDate(existingSelectedDate);
        setSelectedDate(selected ? safeDateFormat(selected) : '');
      } catch (error) {
        console.error('Error setting existing selected date:', error);
        setError('Error processing selected date');
      }
    }
  }, [proposedDates, existingSelectedDate, convertToDate, safeDateFormat]);

  const validateDates = useCallback((): boolean => {
    const dates = [preferredDate, secondaryDate, tertiaryDate].filter(Boolean);
    const uniqueDates = new Set(dates);

    if (uniqueDates.size !== dates.length) {
      setError('Each date must be unique. Please select different dates.');
      return false;
    }

    const allDatesValid = dates.every(date => {
      try {
        const parsedDate = parseISO(date);
        if (!isValid(parsedDate)) return false;

        const parsedStartOfDay = startOfDay(parsedDate);
        return parsedStartOfDay >= today && parsedStartOfDay <= parseISO(maxDate);
      } catch (error) {
        console.error('Error validating date:', error);
        return false;
      }
    });

    if (!allDatesValid) {
      setError(`Please select dates between ${formatDisplayDate(today)} and ${formatDisplayDate(parseISO(maxDate))}`);
      return false;
    }

    return true;
  }, [preferredDate, secondaryDate, tertiaryDate, maxDate, today, formatDisplayDate]);

  const handleDateSubmit = useCallback(() => {
    if (validateDates()) {
      onDatesSet({
        preferred: preferredDate,
        secondary: secondaryDate,
        tertiary: tertiaryDate
      });
      toggleModal();
    }
  }, [preferredDate, secondaryDate, tertiaryDate, onDatesSet, toggleModal, validateDates]);

  const handleDateSelection = useCallback(() => {
    if (selectedDate) {
      onDateSelected(selectedDate);
      toggleModal();
    }
  }, [selectedDate, onDateSelected, toggleModal]);

  const renderProposedDates = useCallback(() => {
    if (!proposedDates) return null;

    const options = [];
    options.push(<option key="default" value="">Select a date...</option>);

    try {
      if (proposedDates.preferred) {
        const preferred = convertToDate(proposedDates.preferred);
        if (preferred && isValid(preferred)) {
          options.push(
            <option key="preferred" value={safeDateFormat(preferred)}>
              {formatDisplayDate(preferred)} (Preferred)
            </option>
          );
        }
      }

      if (proposedDates.secondary) {
        const secondary = convertToDate(proposedDates.secondary);
        if (secondary && isValid(secondary)) {
          options.push(
            <option key="secondary" value={safeDateFormat(secondary)}>
              {formatDisplayDate(secondary)}
            </option>
          );
        }
      }

      if (proposedDates.tertiary) {
        const tertiary = convertToDate(proposedDates.tertiary);
        if (tertiary && isValid(tertiary)) {
          options.push(
            <option key="tertiary" value={safeDateFormat(tertiary)}>
              {formatDisplayDate(tertiary)}
            </option>
          );
        }
      }
    } catch (error) {
      console.error('Error rendering proposed dates:', error);
      setError('Error displaying proposed dates');
    }

    return options;
  }, [proposedDates, convertToDate, safeDateFormat, formatDisplayDate]);

  if (!hasInspectionMilestones) {
    return null;
  }

  return (
    <Modal isOpen={isModalOpen} toggle={toggleModal} backdrop="static">
      <ModalHeader toggle={toggleModal}>
        {isContractor ? 'Set Inspection Dates' : 'Select Inspection Date'}
      </ModalHeader>
      <ModalBody>
        {error && <Alert color="danger">{error}</Alert>}

        {isContractor ? (
          <>
            <FormGroup>
              <Label for="preferredDate">Preferred Date</Label>
              <Input
                type="date"
                id="preferredDate"
                value={preferredDate}
                onChange={(e) => setPreferredDate(e.target.value)}
                min={safeDateFormat(today)}
                max={maxDate}
              />
            </FormGroup>
            <FormGroup>
              <Label for="secondaryDate">Secondary Date</Label>
              <Input
                type="date"
                id="secondaryDate"
                value={secondaryDate}
                onChange={(e) => setSecondaryDate(e.target.value)}
                min={safeDateFormat(today)}
                max={maxDate}
              />
            </FormGroup>
            <FormGroup>
              <Label for="tertiaryDate">Tertiary Date</Label>
              <Input
                type="date"
                id="tertiaryDate"
                value={tertiaryDate}
                onChange={(e) => setTertiaryDate(e.target.value)}
                min={safeDateFormat(today)}
                max={maxDate}
              />
            </FormGroup>
          </>
        ) : (
          <FormGroup>
            <Label for="inspectionDate">Choose a Date</Label>
            <Input
              type="select"
              id="inspectionDate"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            >
              {renderProposedDates()}
            </Input>
          </FormGroup>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggleModal}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={isContractor ? handleDateSubmit : handleDateSelection}
          disabled={isContractor ?
            (!preferredDate || !secondaryDate || !tertiaryDate) :
            !selectedDate}
        >
          {isContractor ? 'Submit Dates' : 'Confirm Date'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default InspectionMilestones;